import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, TextInput, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { getToteSummary } from './utils/scanningUtils';
import useToteStatus from './useToteStatus';
import CamScanner from '../camScanning/CamScanner';
import useEnterKeySubmit from '../../commons/hooks/useEnterKeySubmit';

const MultiToteVerificationPrompt = ({
  toteSummary,
  onVerify,
  onClose,
  isLoading = false,
  windowWidth,
  camScannerEnabled = false,
  showCameraScanner = false,
  onCameraOpen,
  onCameraClose,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef();
  const isDesktop = (windowWidth || 0) >= 900;

  const { toteAssignments } = useSelector(state => state.scanToCart);
  const { nextPickOrderId } = useToteStatus();

  const liveToteSummary = nextPickOrderId && toteAssignments
    ? getToteSummary(toteAssignments, nextPickOrderId)
    : toteSummary;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleInputChange = (text) => {
    setInputValue(text);
  };

  const handleVerify = () => {
    if (inputValue.trim()) {
      onVerify(inputValue.trim());
      setInputValue('');
    }
  };

  const { onKeyPress, onSubmitEditing } = useEnterKeySubmit(handleVerify);

  const handleCameraScan = (scannedValue) => {
    if (scannedValue && scannedValue.trim()) {
      onVerify(scannedValue.trim());
    }
  };

  return (
    <View style={styles.responseView}>
      <LinearGradient
        locations={[0, 1]}
        start={{ x: 0, y: 1 }}
        end={{ x: 0, y: 0 }}
        colors={['#d47d02', '#ffb636']}
        style={[isDesktop ? styles.promptContainer : styles.promptContainerMobile, { overflow: 'hidden' }]}>

        <View style={styles.cancelIcon}>
          <TouchableOpacity testID="CancelIcon" onPress={onClose}>
            <i className="icon-cancel" style={{ fontSize: 26, color: '#1C1C1C' }}></i>
          </TouchableOpacity>
        </View>

        <ScrollView
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ paddingBottom: 10 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.titleContainer}>
            <Text style={[isDesktop ? styles.titleText : styles.titleTextMobile]}>
              Multi-Tote Order
            </Text>
          </View>

          <View style={styles.toteListContainer}>
            {liveToteSummary.map((tote, index) => (
              <View key={index} style={styles.toteRow}>
                <Text style={[isDesktop ? styles.toteText : styles.toteTextMobile]}>
                  {tote?.tote_id} - Collect {tote?.total_qty} {tote?.total_qty === 1 ? 'Item' : 'Items'}
                </Text>
                {tote.isPrinted && (
                  <View style={styles.checkmarkContainer}>
                    <Text style={styles.checkmark}>✓</Text>
                  </View>
                )}
              </View>
            ))}
          </View>

          <View style={styles.inputContainer}>
            {isLoading ? (
              <ActivityIndicator size="large" color="#1C1C1C" />
            ) : (
              <View style={{ position: 'relative', width: '100%' }}>
                <TextInput
                  ref={inputRef}
                  style={[isDesktop ? styles.input : styles.inputMobile]}
                  value={inputValue}
                  onChangeText={handleInputChange}
                  onSubmitEditing={onSubmitEditing}
                  onKeyPress={onKeyPress}
                  placeholder="Scan tote to verify"
                  placeholderTextColor="#666"
                  autoFocus={true}
                  returnKeyType="done"
                />
                {camScannerEnabled && (
                  <TouchableOpacity
                    testID="modalCameraScanBtn"
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: [{translateY: -12}],
                    }}
                    onPress={onCameraOpen}>
                    <Ionicons
                      name="camera"
                      size={24}
                      color="#666"
                    />
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>

          {camScannerEnabled && showCameraScanner && (
            <View style={{ width: '100%', marginTop: 10 }}>
              <CamScanner
                inline={true}
                onScan={handleCameraScan}
                onClose={onCameraClose}
              />
            </View>
          )}
        </ScrollView>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  responseView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 99999,
    width: '100%',
    height: '100%'
  },
  promptContainer: {
    backgroundColor: '#d3dfea',
    paddingHorizontal: 20,
    paddingVertical: 15,
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    marginTop: 0,
    borderColor: '#e8971a',
    borderWidth: 3,
    borderRadius: 10,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    position: 'relative'
  },
  promptContainerMobile: {
    backgroundColor: '#d3dfea',
    paddingHorizontal: 15,
    paddingVertical: 12,
    width: '90%',
    maxWidth: 400,
    maxHeight: '85%',
    marginTop: '10%',
    borderColor: '#e8971a',
    borderWidth: 3,
    borderRadius: 10,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    position: 'relative'
  },
  cancelIcon: {
    display: 'flex',
    alignSelf: 'flex-end'
  },
  titleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    marginTop: 10
  },
  titleText: {
    fontFamily: 'Poppins_300Light',
    fontSize: 20,
    textAlign: 'center',
    color: '#000',
    fontWeight: '500'
  },
  titleTextMobile: {
    fontFamily: 'Poppins_300Light',
    fontSize: 18,
    textAlign: 'center',
    color: '#000',
    fontWeight: '500'
  },
  toteListContainer: {
    marginBottom: 20
  },
  toteRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFB636',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  toteText: {
    flex: 1,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
  },
  toteTextMobile: {
    flex: 1,
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
  },
  checkmarkContainer: {
    backgroundColor: '#39CE8D',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10,
  },
  checkmark: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  inputContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderWidth: 2,
    borderColor: '#e8971a',
    borderRadius: 8,
    padding: 12,
    width: '100%',
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    textAlign: 'center',
    fontSize: 16,
  },
  inputMobile: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderWidth: 2,
    borderColor: '#e8971a',
    borderRadius: 8,
    padding: 10,
    width: '100%',
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    textAlign: 'center',
    fontSize: 14,
  },
});

export default MultiToteVerificationPrompt;
